import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import maplibregl from 'maplibre-gl';

import { usePropertyStore } from '@/stores/property-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { CoordinateDisplay } from '@/components/map/CoordinateDisplay';
import { MapPickerModal } from '@/components/map/MapPickerModal';
import { BoundaryDrawingModal } from '@/components/map/BoundaryDrawingModal';
import { HuntMap } from '@/components/map/HuntMap';
import { WaypointMetadataForm } from '@/components/waypoint/WaypointMetadataForm';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { WAYPOINT_TYPE_LABELS, WAYPOINT_TYPE_COLORS } from '@/components/map/map-utils';
import type { WaypointMetadata, GeoJSONLineString, GeoJSONPolygon } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';
import { computeAcreage } from '@/utils/boundary-utils';
import { useMap } from '@/hooks/useMap';

// ---------------------------------------------------------------------------
// BoundaryPreviewLayer — adds a read-only polygon overlay to a HuntMap
// ---------------------------------------------------------------------------

interface BoundaryPreviewLayerProps {
  polygon: GeoJSONPolygon;
  color: string;
  center: [number, number];
}

function BoundaryPreviewLayer({ polygon, color, center }: BoundaryPreviewLayerProps) {
  const map = useMap();
  const markerRef = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    const SOURCE_ID = 'bdry-preview-src';
    const FILL_ID = 'bdry-preview-fill';
    const LINE_ID = 'bdry-preview-line';

    function setup() {
      try {
        if (!map.getSource(SOURCE_ID)) {
          map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: { type: 'Feature', geometry: polygon, properties: {} },
          });
        }
        if (!map.getLayer(FILL_ID)) {
          map.addLayer({
            id: FILL_ID,
            type: 'fill',
            source: SOURCE_ID,
            paint: { 'fill-color': color, 'fill-opacity': 0.2 },
          });
        }
        if (!map.getLayer(LINE_ID)) {
          map.addLayer({
            id: LINE_ID,
            type: 'line',
            source: SOURCE_ID,
            paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.85 },
          });
        }
        const coords = polygon.coordinates[0];
        const lngs = coords.map(([lng]) => lng);
        const lats = coords.map(([, lat]) => lat);
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 32, animate: false },
        );
        if (!markerRef.current) {
          markerRef.current = new maplibregl.Marker({ color })
            .setLngLat([center[1], center[0]])
            .addTo(map);
        }
      } catch { /* map may not be ready */ }
    }

    map.on('style.load', setup);
    if (map.isStyleLoaded()) setup();

    return () => {
      map.off('style.load', setup);
      markerRef.current?.remove();
      markerRef.current = null;
      try {
        if (map.getLayer(LINE_ID)) map.removeLayer(LINE_ID);
        if (map.getLayer(FILL_ID)) map.removeLayer(FILL_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch { /* map destroyed */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygon, color]);

  return null;
}

// ---------------------------------------------------------------------------

function parseWaypointType(raw: string | null): WaypointType {
  if (raw && Object.values(WaypointType).includes(raw as WaypointType)) {
    return raw as WaypointType;
  }
  return WaypointType.Blind;
}

export function WaypointFormPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reactLocation = useLocation();

  const waypointType = parseWaypointType(searchParams.get('type'));

  const { properties, loadProperties } = usePropertyStore();
  const { addWaypoint, waypoints } = useWaypointStore();

  const [name, setName] = useState('');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [metadata, setMetadata] = useState<Partial<WaypointMetadata>>({});
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [boundaryModalOpen, setBoundaryModalOpen] = useState(false);
  const [showClearBoundaryConfirm, setShowClearBoundaryConfirm] = useState(false);

  // ROOT-CAUSE (I-3): ScrollToTop fires on pathname change, but form content renders
  // asynchronously (map tiles, metadata fields) which causes layout shifts that scroll
  // the viewport down after ScrollToTop has already set position to 0. An explicit
  // mount-time scroll-to-top pre-empts any post-render layout shift.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (properties.length === 0) {
      void loadProperties();
    }
  }, [properties.length, loadProperties]);

  const property = properties.find((p) => p.id === propertyId);

  // Handle trail result returned from TrailDrawPage
  useEffect(() => {
    const state = reactLocation.state as { trailResult?: GeoJSONLineString; trailAnchor?: [number, number]; locationName?: string } | null;
    if (!state?.trailResult) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
    setMetadata((prev) => ({ ...prev, pathGeoJSON: state.trailResult }));
    if (!coordinates && state.trailAnchor) {
      setCoordinates({ lat: state.trailAnchor[0], lng: state.trailAnchor[1] });
    }
    if (state.locationName) {
      setName(state.locationName);
    }
    navigate(reactLocation.pathname + reactLocation.search, { replace: true, state: {} });
  }, [reactLocation.state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!propertyId) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-[var(--color-text-placeholder)] text-body-sm">Invalid URL.</p>
        <Link to="/land/properties" className="text-[var(--color-accent)] text-body-sm hover:text-[var(--color-accent-hover)] transition-colors">
          ← Properties
        </Link>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-body-sm text-[var(--color-text-muted)]">Loading…</p>
      </div>
    );
  }

  const typeLabel = WAYPOINT_TYPE_LABELS[waypointType];
  const typeColor = WAYPOINT_TYPE_COLORS[waypointType];

  // Boundary helpers — only relevant for FoodSource waypoints
  const isFoodSource = waypointType === WaypointType.FoodSource;
  const meta = metadata as Record<string, unknown>;
  const boundaryGeoJSON = meta.boundaryGeoJSON as GeoJSONPolygon | undefined;
  const hasBoundary = !!boundaryGeoJSON;
  const boundaryAcres = hasBoundary
    ? computeAcreage(
        boundaryGeoJSON.coordinates[0].map(([lng, lat]) => [lat, lng] as [number, number]),
      )
    : null;

  const boundaryVertices: [number, number][] | undefined = hasBoundary
    ? boundaryGeoJSON.coordinates[0]
        .slice(0, -1)
        .map(([lng, lat]) => [lat, lng] as [number, number])
    : undefined;

  function handleBoundarySave(vertices: [number, number][]) {
    const coords: [number, number][] = vertices.map(([lat, lng]) => [lng, lat]);
    const closed: [number, number][] = [...coords, coords[0]];
    const polygon: GeoJSONPolygon = { type: 'Polygon', coordinates: [closed] };
    const acresCalc = computeAcreage(vertices);
    setMetadata((prev) => ({
      ...prev,
      boundaryGeoJSON: polygon,
      plotSizeAcresCalculated: acresCalc,
    }));
    setBoundaryModalOpen(false);
  }

  function handleClearBoundary() {
    setMetadata((prev) => {
      const next = { ...(prev as Record<string, unknown>) };
      delete next.boundaryGeoJSON;
      delete next.plotSizeAcresCalculated;
      return next as Partial<WaypointMetadata>;
    });
    setShowClearBoundaryConfirm(false);
  }

  async function handleSave() {
    if (!name.trim() || !coordinates) return;
    setIsSaving(true);
    // Snapshot BEFORE await so we capture the pre-save state
    const isFirstWaypoint = waypoints.length === 0;
    const fromOnboarding = (reactLocation.state as { fromOnboarding?: boolean } | null)?.fromOnboarding === true;
    try {
      await addWaypoint({
        propertyId: property.id,
        name: name.trim(),
        type: waypointType,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        notes: '',
        metadata: metadata as WaypointMetadata,
      });
      if (isFirstWaypoint && fromOnboarding) {
        navigate('/', {
          replace: true,
          state: { toastMessage: 'Waypoint pinned. System online.' },
        });
      } else {
        navigate(`/land/properties/${property.id}`, { replace: true });
      }
    } catch (err) {
      console.error('WaypointFormPage.handleSave:', err);
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      {/* Back + header */}
      <div className="flex flex-col gap-1">
        <Link
          to={`/land/properties/${property.id}`}
          className="text-[var(--color-accent)] text-body-sm hover:text-[var(--color-accent-hover)] transition-colors"
        >
          ← {property.name}
        </Link>
        <div className="flex items-center gap-3 mt-1">
          <h1 className="font-display text-display-lg text-[var(--color-text-primary)] flex-1">New Waypoint</h1>
          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-body-xs font-medium border border-[var(--color-border-subtle)] text-[var(--color-text-muted)] shrink-0">
            {typeLabel}
          </span>
        </div>
      </div>

      {/* Name */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4">
        <Input
          id="waypoint-name"
          label="Waypoint Name"
          value={name}
          onChange={setName}
          placeholder={`Enter ${typeLabel.toLowerCase()} name…`}
          required
        />
      </section>

      {/* Coordinates */}
      <CoordinateDisplay
        lat={coordinates?.lat ?? null}
        lng={coordinates?.lng ?? null}
        onPickLocation={() => setMapPickerOpen(true)}
        label="Location"
      />

      <MapPickerModal
        isOpen={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        onConfirm={(coords) => {
          setCoordinates(coords);
          setMapPickerOpen(false);
        }}
        initialCoordinates={coordinates ?? (property ? { lat: property.latitude, lng: property.longitude } : undefined)}
        initialStyle={waypointType === WaypointType.GameTrail ? 'satellite' : undefined}
        title={`Pick ${typeLabel} Location`}
      />

      {/* Boundary section — FoodSource only, only after location is set */}
      {isFoodSource && coordinates && (
        <>
          <section
            className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card overflow-hidden flex flex-col gap-0"
            data-testid="boundary-section"
          >
            {hasBoundary && (
              <div className="relative" style={{ height: '200px' }} data-testid="boundary-mini-map">
                <HuntMap
                  center={[coordinates.lat, coordinates.lng]}
                  zoom={15}
                  className="h-full w-full"
                  initialStyle="satellite"
                  requireTapToInteract
                >
                  <BoundaryPreviewLayer
                    polygon={boundaryGeoJSON}
                    color={typeColor}
                    center={[coordinates.lat, coordinates.lng]}
                  />
                </HuntMap>
              </div>
            )}

            <div className="p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h2 className="field-label">
                  Boundary
                </h2>
                {hasBoundary && boundaryAcres !== null && (
                  <span
                    className="text-body-xs font-medium bg-[var(--color-elevated)] border border-[var(--color-border)] px-2 py-0.5 rounded text-[var(--color-text-secondary)]"
                    data-testid="acreage-badge"
                  >
                    ~{boundaryAcres.toFixed(1)} acres
                  </span>
                )}
              </div>

              {hasBoundary ? (
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setBoundaryModalOpen(true)} data-testid="edit-boundary-button">
                    Edit Boundary
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowClearBoundaryConfirm(true)}
                    className="text-body-sm text-[var(--color-text-placeholder)] hover:text-[var(--color-accent)] transition-colors"
                    data-testid="clear-boundary-button"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-body-xs text-[var(--color-text-placeholder)]">Draw a boundary to calculate area</p>
                  <Button variant="ghost" onClick={() => setBoundaryModalOpen(true)} data-testid="draw-boundary-button">
                    Draw Boundary
                  </Button>
                </div>
              )}
            </div>
          </section>

          {boundaryModalOpen && (
            <BoundaryDrawingModal
              isOpen={boundaryModalOpen}
              onClose={() => setBoundaryModalOpen(false)}
              onSave={handleBoundarySave}
              center={[coordinates.lat, coordinates.lng]}
              initialVertices={boundaryVertices}
            />
          )}

          {showClearBoundaryConfirm && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
              role="dialog"
              aria-modal="true"
            >
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 max-w-sm w-full shadow-lg">
                <h2 className="font-display text-display-sm text-[var(--color-text-display)] mb-2">Clear boundary?</h2>
                <p className="text-body-sm text-[var(--color-text-muted)] mb-5">
                  This will remove the drawn boundary and calculated acreage. You can redraw it later.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setShowClearBoundaryConfirm(false)}>Cancel</Button>
                  <Button variant="danger" onClick={handleClearBoundary} data-testid="confirm-clear-boundary">Clear</Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Type-specific metadata */}
      <section
        className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4"
        data-testid="details-section"
      >
        <h2 className="field-label mb-4">
          Details
        </h2>
        <WaypointMetadataForm
          type={waypointType}
          metadata={metadata}
          onChange={setMetadata}
          propertyId={property.id}
          centerCoords={
            coordinates && !isFoodSource
              ? [coordinates.lat, coordinates.lng]
              : undefined
          }
          locationName={name}
        />
      </section>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="ghost"
          onClick={() => void navigate(`/land/properties/${property.id}`)}
          className="flex-1 justify-center"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          disabled={isSaving || !name.trim() || !coordinates}
          className="flex-1 justify-center"
        >
          {isSaving ? 'Saving…' : 'Save Waypoint'}
        </Button>
      </div>
    </div>
  );
}
